import { router } from "@inertiajs/react";
import { Fragment, useEffect, useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle, CheckSquare, Clock, RefreshCw } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { useDncSync } from "../../hooks/useDncSync";

const CANONICAL_CITIES = [
  "Los Angeles", "New York", "Washington", "Chicago", "Boston",
  "San Francisco", "Houston", "Dallas", "Phoenix", "Seattle",
  "Austin", "Atlanta", "Miami", "Denver", "Philadelphia",
  "Palo Alto", "Menlo Park", "Costa Mesa", "El Segundo", "New Orleans",
  "Charleston", "Alexandria", "Torrance", "Southlake", "Remote",
];

const SCOPE_TYPE_OPTIONS = [
  { value: "nationwide",      label: "Nationwide" },
  { value: "office",          label: "Office (specific city)" },
  { value: "office_practice", label: "Office & Practice Area" },
  { value: "unknown",         label: "Unknown" },
  { value: "not_applicable",  label: "Not a DNC (does not apply to me)" },
];

type ScopeType = "nationwide" | "office" | "office_practice" | "unknown" | "not_applicable";
type Tab = "active" | "needs_review" | "expired";

interface DncRecord {
  id: number;
  firm_name: string;
  placement_office: string;
  practice_area: string;
  scope: string;
  scope_type: ScopeType | null;
  normalized_city: string | null;
  scope_needs_review: boolean;
  scope_role_restriction: string | null;
  scope_resolved_by: string | null;
  scope_resolved_at: string | null;
  reason: string;
  resolution_reason: string | null;
  start_date_raw: string;
  end_date_raw: string;
  is_ongoing: boolean;
  forfeiture: boolean;
  placed_candidates: string;
  recruiter: string;
  notes: string;
  expired: boolean;
  deleted_at: string | null;
}

interface SyncLog {
  status: "running" | "completed" | "failed" | "partial";
  triggered_by: string;
  started_at: string | null;
  completed_at: string | null;
  records_upserted: number;
  records_skipped: number;
  error_message: string | null;
  duration_seconds: number | null;
}

interface Props {
  records: DncRecord[];
  last_sync: SyncLog | null;
  needs_review_count: number;
  include_deleted: boolean;
}

function ScopeTypeBadge({ scopeType }: { scopeType: ScopeType | null }) {
  if (!scopeType) return null;
  const styles: Record<ScopeType, string> = {
    nationwide:      "bg-red-100 text-red-700",
    office:          "bg-blue-100 text-blue-700",
    office_practice: "bg-purple-100 text-purple-700",
    unknown:         "bg-amber-100 text-amber-700",
    not_applicable:  "bg-green-100 text-green-700",
  };
  const labels: Record<ScopeType, string> = {
    nationwide:      "Nationwide",
    office:          "Office",
    office_practice: "Office + Practice",
    unknown:         "Unknown",
    not_applicable:  "Not a DNC",
  };
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${styles[scopeType]}`}>
      {labels[scopeType]}
    </span>
  );
}

function ResolveForm({ record, onClose }: { record: DncRecord; onClose: () => void }) {
  const [scopeType, setScopeType] = useState<ScopeType>(record.scope_type ?? "unknown");
  const [city, setCity] = useState(record.normalized_city ?? "");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const needsCity    = scopeType === "office" || scopeType === "office_practice";
  const notApplicable = scopeType === "not_applicable";
  const submitDisabled = submitting
    || (needsCity && !city.trim())
    || (notApplicable && !reason.trim());

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    router.patch(
      `/dnc/${record.id}/resolve`,
      {
        scope_type:        scopeType,
        normalized_city:   needsCity ? city : "",
        resolution_reason: notApplicable ? reason : "",
      },
      { onSuccess: onClose, onFinish: () => setSubmitting(false) }
    );
  }

  return (
    <div className="px-5 py-4 bg-amber-50 border-t border-amber-200">
      <p className="text-xs text-amber-700 mb-3">
        <span className="font-medium">Original scope from sheet:</span>{" "}
        <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-amber-200 text-gray-700">
          {record.scope || "(empty)"}
        </span>
        {record.scope_role_restriction && (
          <span className="ml-3 text-amber-600">
            Role restriction: <span className="font-medium">{record.scope_role_restriction.replace(/_/g, " ")}</span>
          </span>
        )}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1 font-medium">Scope type</label>
          <select
            value={scopeType}
            onChange={(e) => setScopeType(e.target.value as ScopeType)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {SCOPE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {needsCity && (
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">City</label>
            <input
              type="text"
              list="canonical-cities"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Los Angeles"
              className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
            />
            <datalist id="canonical-cities">
              {CANONICAL_CITIES.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
        )}

        {notApplicable && (
          <div className="w-full">
            <label className="block text-xs text-gray-500 mb-1 font-medium">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. I am the recruiter of record for this placement"
              rows={2}
              className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-full max-w-lg"
            />
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitDisabled}
            className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Saving..." : "Save Resolution"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function SyncStatusBanner({
  sync,
  liveRunning,
  onSyncClick,
}: {
  sync: SyncLog | null;
  liveRunning: boolean;
  onSyncClick: () => void;
}) {
  const handleSync = () => {
    onSyncClick();
    router.post("/dnc/sync", {}, { preserveScroll: true });
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  };

  return (
    <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-5 text-sm">
      <div className="flex items-center gap-3">
        {!sync && !liveRunning && (
          <span className="text-gray-400 flex items-center gap-1.5">
            <Clock size={14} /> Never synced
          </span>
        )}
        {(liveRunning || sync?.status === "running") && (
          <span className="text-blue-600 flex items-center gap-1.5">
            <RefreshCw size={14} className="animate-spin" /> Sync in progress...
          </span>
        )}
        {sync?.status === "completed" && (
          <span className="text-green-700 flex items-center gap-1.5">
            <CheckCircle size={14} />
            Last synced {formatTime(sync.completed_at)}
            <span className="text-gray-400 font-normal">
              &middot; {sync.records_upserted} records
              {sync.duration_seconds != null && ` in ${sync.duration_seconds}s`}
            </span>
          </span>
        )}
        {sync?.status === "failed" && (
          <span className="text-red-600 flex items-center gap-1.5">
            <AlertCircle size={14} />
            Sync failed {formatTime(sync.started_at)}
            {sync.error_message && (
              <span className="text-gray-500 font-normal">&middot; {sync.error_message}</span>
            )}
          </span>
        )}
        {sync?.status === "partial" && (
          <span className="text-amber-700 flex items-center gap-1.5" title={sync.error_message ?? ""}>
            <AlertTriangle size={14} />
            Partial sync {formatTime(sync.completed_at)}
            <span className="text-gray-500 font-normal">
              &middot; {sync.records_upserted} upserted, deletion skipped
            </span>
          </span>
        )}
      </div>

      <button
        onClick={handleSync}
        disabled={liveRunning || sync?.status === "running"}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <RefreshCw size={12} className={(liveRunning || sync?.status === "running") ? "animate-spin" : ""} />
        Sync Now
      </button>
    </div>
  );
}

function formatResolutionDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function DncRecordsIndex({ records, last_sync, needs_review_count, include_deleted }: Props) {
  const [tab, setTab] = useState<Tab>(needs_review_count > 0 ? "needs_review" : "active");
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const { syncing, setSyncing } = useDncSync();

  const active      = records.filter((r) => !r.expired && !r.scope_needs_review);
  const needsReview = records.filter((r) => !r.expired && r.scope_needs_review);
  const expired     = records.filter((r) => r.expired);
  const tabRecords  = tab === "active" ? active : tab === "needs_review" ? needsReview : expired;

  function toggleIncludeDeleted() {
    router.visit(include_deleted ? "/dnc" : "/dnc?include_deleted=true", { preserveScroll: true });
  }

  function restoreRecord(id: number) {
    router.post(`/dnc/${id}/restore`, {}, { preserveScroll: true });
  }

  function formatDeletedAt(iso: string) {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  useEffect(() => {
    if (last_sync?.status !== "running") return;
    const interval = setInterval(() => {
      router.reload({ only: ["last_sync", "records", "needs_review_count"] });
    }, 3000);
    return () => clearInterval(interval);
  }, [last_sync?.status]);

  const tabClass = (t: Tab) =>
    `px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
      tab === t
        ? "border-gray-900 text-gray-900"
        : "border-transparent text-gray-500 hover:text-gray-700"
    }`;

  const colSpan = tab === "needs_review" ? 9 : 8;

  return (
    <AppLayout title="DNC List">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-800">Do Not Contact</h1>
        <p className="text-sm text-gray-500 mt-1">
          {active.length} active restriction{active.length !== 1 ? "s" : ""}
          {needs_review_count > 0 && `, ${needs_review_count} need${needs_review_count !== 1 ? "" : "s"} review`}
          {expired.length > 0 && `, ${expired.length} expired`}
        </p>
      </div>

      <SyncStatusBanner sync={last_sync} liveRunning={syncing} onSyncClick={() => setSyncing(true)} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Tab nav */}
        <div className="flex border-b border-gray-200 px-2 items-center">
          <button className={tabClass("active")} onClick={() => setTab("active")}>
            Active
            <span className="ml-1.5 text-xs text-gray-400">({active.length})</span>
          </button>

          <button className={tabClass("needs_review")} onClick={() => setTab("needs_review")}>
            <span className="flex items-center gap-1.5">
              {needs_review_count > 0 && <AlertTriangle size={13} className="text-amber-500" />}
              Needs Review
              {needs_review_count > 0 ? (
                <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">
                  {needs_review_count}
                </span>
              ) : (
                <span className="ml-1 text-xs text-gray-400">(0)</span>
              )}
            </span>
          </button>

          <button className={tabClass("expired")} onClick={() => setTab("expired")}>
            Expired
            <span className="ml-1.5 text-xs text-gray-400">({expired.length})</span>
          </button>

          <label className="ml-auto flex items-center gap-1.5 px-3 text-xs text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={include_deleted}
              onChange={toggleIncludeDeleted}
              className="rounded border-gray-300 text-gray-700 focus:ring-gray-400"
            />
            Show soft-deleted
          </label>
        </div>

        {/* Table */}
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Firm</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Practice Area</th>
              <th className="px-4 py-3 text-left">Start</th>
              <th className="px-4 py-3 text-left">End</th>
              <th className="px-4 py-3 text-left">Recruiter</th>
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-left">Status</th>
              {tab === "needs_review" && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tabRecords.map((r) => (
              <Fragment key={r.id}>
                <tr className={r.deleted_at ? "bg-gray-50 opacity-60" : r.expired ? "opacity-50" : ""}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {r.firm_name}
                    {r.forfeiture && (
                      <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                        Forfeiture
                      </span>
                    )}
                    {r.deleted_at && (
                      <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                        Soft-deleted {formatDeletedAt(r.deleted_at)}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 items-start">
                      <ScopeTypeBadge scopeType={r.scope_type} />
                      {(r.scope_type === "office" || r.scope_type === "office_practice") && r.normalized_city && (
                        <span className="text-xs text-gray-500">{r.normalized_city}</span>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-gray-600">{r.practice_area || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{r.start_date_raw || "—"}</td>

                  <td className="px-4 py-3 text-gray-600">
                    {r.is_ongoing ? (
                      <span className="text-amber-600 font-medium">Ongoing</span>
                    ) : (
                      r.end_date_raw || "—"
                    )}
                  </td>

                  <td className="px-4 py-3 text-gray-600">{r.recruiter || "—"}</td>

                  <td className="px-4 py-3">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">Sheet</span>
                  </td>

                  <td className="px-4 py-3">
                    {r.deleted_at ? (
                      <button
                        onClick={() => restoreRecord(r.id)}
                        className="text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 rounded px-2 py-1 transition-colors"
                      >
                        Restore
                      </button>
                    ) : r.expired ? (
                      <span className="text-xs text-gray-400">Expired</span>
                    ) : r.scope_needs_review ? (
                      <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                        <AlertTriangle size={12} /> Needs Review
                      </span>
                    ) : r.scope_resolved_by ? (
                      <div>
                        {r.scope_type === "not_applicable" ? (
                          <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                            <CheckCircle size={12} /> Not a DNC
                          </span>
                        ) : (
                          <span className="text-xs text-red-600 font-medium">Active</span>
                        )}
                        <span className="block text-xs text-gray-400 mt-0.5">
                          Resolved by {r.scope_resolved_by}
                          {r.scope_resolved_at && ` · ${formatResolutionDate(r.scope_resolved_at)}`}
                        </span>
                        {r.scope_type === "not_applicable" && r.resolution_reason && (
                          <span className="block text-xs text-gray-500 mt-0.5 italic">{r.resolution_reason}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-red-600 font-medium">Active</span>
                    )}
                  </td>

                  {tab === "needs_review" && (
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setResolvingId(resolvingId === r.id ? null : r.id)}
                        className="text-xs px-2.5 py-1 rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors font-medium"
                      >
                        {resolvingId === r.id ? "Cancel" : "Resolve"}
                      </button>
                    </td>
                  )}
                </tr>

                {resolvingId === r.id && (
                  <tr>
                    <td colSpan={colSpan}>
                      <ResolveForm record={r} onClose={() => setResolvingId(null)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}

            {tabRecords.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-400">
                  {tab === "needs_review" ? (
                    <span className="flex items-center justify-center gap-2 text-green-600">
                      <CheckSquare size={16} />
                      All scopes resolved — nothing needs review.
                    </span>
                  ) : tab === "active" ? (
                    "No active DNC restrictions."
                  ) : (
                    "No expired DNC records."
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
